import {
  addSessionStorageItem,
  deletesessionStorageItem,
  getsessionStorageItem,
} from "../helpers/sessionStorageHelper";
import { SessionStorageKeys } from "./constants";
import { aesCtrDecryptNoSpecialChars } from "./common";

// Query params used by private list pages to auto-open linked files.
export const AGREEMENT_DEEP_LINK_QUERY_PARAM = "agreement";
export const DOCUMENT_DEEP_LINK_QUERY_PARAM = "doc";

export const getCurrentDeepLinkUrl = () => {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
};

// Keep the original deeplink while the user is redirected through login/setup.
export const setPendingDeepLinkUrl = (url = getCurrentDeepLinkUrl()) => {
  if (!url) return;
  addSessionStorageItem(SessionStorageKeys.PendingDeepLinkUrl, url);
};

export const getPendingDeepLinkUrl = () =>
  getsessionStorageItem(SessionStorageKeys.PendingDeepLinkUrl, "");

export const clearPendingDeepLinkUrl = () => {
  deletesessionStorageItem(SessionStorageKeys.PendingDeepLinkUrl);
};

export const consumePendingDeepLinkUrl = () => {
  const url = getPendingDeepLinkUrl();
  clearPendingDeepLinkUrl();
  return url;
};

export const clearUrlQueryParam = (paramName) => {
  const url = new URL(window.location);
  url.searchParams.delete(paramName);
  window.history.replaceState({}, "", url);
};

export const getPathWithQueryParam = (path, paramName, value) => {
  const url = new URL(path, window.location.origin);
  url.searchParams.set(paramName, value);
  return `${url.pathname}${url.search}${url.hash}`;
};

// Agreement file links are AES-CTR encrypted. Supports old 4-part links and new 6-part links.
export const parseAgreementDeepLinkPayload = async (token) => {
  const decryptedId = await aesCtrDecryptNoSpecialChars(token);
  const parts = (decryptedId || "").split(":");

  if (![4, 6].includes(parts.length)) {
    throw new Error("Invalid agreement link.");
  }

  const parsedParts = parts.map((part) => parseInt(part, 10));
  const agreementId = parsedParts[0];
  const accountId = parsedParts[1];
  const profileId = parsedParts[2];
  const sharedAccountId = parts.length === 6 ? parsedParts[3] : 0;
  const sharedProfileId = parts.length === 6 ? parsedParts[4] : 0;
  const agreementCreatedTime =
    parts.length === 6 ? parsedParts[5] : parsedParts[3];

  if (
    [
      agreementId,
      accountId,
      profileId,
      sharedAccountId,
      sharedProfileId,
      agreementCreatedTime,
    ].some((value) => Number.isNaN(value))
  ) {
    throw new Error("Invalid agreement link.");
  }

  return {
    agreementId,
    accountId,
    profileId,
    sharedAccountId,
    sharedProfileId,
    agreementCreatedTime,
  };
};

// Document file links are AES-CTR encrypted as documentId:accountId:profileId:createdTime.
export const parseDocumentDeepLinkPayload = async (token) => {
  const decryptedId = await aesCtrDecryptNoSpecialChars(token);
  const parts = (decryptedId || "").split(":");

  if (parts.length !== 4) {
    throw new Error("Invalid document link.");
  }

  const parsedParts = parts.map((part) => parseInt(part, 10));
  const documentId = parsedParts[0];
  const accountId = parsedParts[1];
  const profileId = parsedParts[2];
  const documentCreatedTime = parsedParts[3];

  if (
    [documentId, accountId, profileId, documentCreatedTime].some((value) =>
      Number.isNaN(value),
    )
  ) {
    throw new Error("Invalid document link.");
  }

  return {
    documentId,
    accountId,
    profileId,
    documentCreatedTime,
  };
};

// Folder links support root links and nested links:
// folderId:accountId:profileId:createdTime or folderId:accountId:profileId:parentFolderId:createdTime.
export const parseDocumentFolderDeepLinkPayload = async (token) => {
  const decryptedId = await aesCtrDecryptNoSpecialChars(token);
  const parts = (decryptedId || "").split(":");

  if (![4, 5].includes(parts.length)) {
    throw new Error("Invalid document link.");
  }

  const parsedParts = parts.map((part) => parseInt(part, 10));
  const folderId = parsedParts[0];
  const accountId = parsedParts[1];
  const profileId = parsedParts[2];
  const parentFolderId = parts.length === 5 ? parsedParts[3] : 0;
  const folderCreatedDatetime =
    parts.length === 5 ? parsedParts[4] : parsedParts[3];

  if (
    [folderId, accountId, profileId, parentFolderId, folderCreatedDatetime].some(
      (value) => Number.isNaN(value),
    )
  ) {
    throw new Error("Invalid document link.");
  }

  return {
    folderId,
    accountId,
    profileId,
    parentFolderId,
    folderCreatedDatetime,
  };
};
